/**
 * Role Privileges Management - Microsoft 365 Style
 * Comprehensive interface for managing role-based privileges (RBAC)
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  makeStyles,
  Text,
  Input,
  Button,
  Spinner,
  Badge,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  SearchRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ShieldCheckmarkRegular,
  OrganizationRegular,
  PeopleRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import type {
  Feature,
  FeaturePrivilege,
  RolePrivilege,
  GroupedPrivileges,
} from '../../../types/rbac.types';
import type { Role, Department } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
    marginTop: '4px',
  },
  filtersSection: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    alignItems: 'flex-end',
  },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    maxWidth: '300px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '24px',
    minHeight: '600px',
  },
  roleListPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  roleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  roleCard: {
    padding: '12px',
    border: '1px solid #e1dfdd',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  roleCardSelected: {
    backgroundColor: '#e6f7f5',
  },
  roleName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  roleDepartment: {
    fontSize: '12px',
    color: '#605e5c',
    marginTop: '4px',
  },
  privilegePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#605e5c',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '12px',
    borderBottom: '2px solid #e1dfdd',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  featureGroup: {
    marginBottom: '16px',
  },
  featureTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  privilegeList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '8px',
    paddingLeft: '16px',
  },
  privilegeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '4px',
    backgroundColor: '#f3f2f1',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  privilegeItemAssigned: {
    backgroundColor: '#e6f7f5',
    border: '1px solid #008272',
  },
  privilegeText: {
    fontSize: '13px',
    color: '#323130',
    flex: 1,
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  infoBox: {
    padding: '12px 16px',
    backgroundColor: '#f3f9ff',
    border: '1px solid #0078d4',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  infoText: {
    fontSize: '13px',
    color: '#323130',
  },
});

export const RolePrivilegesManagementFluentUI: React.FC = () => {
  const styles = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data states
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [allPrivileges, setAllPrivileges] = useState<FeaturePrivilege[]>([]);
  const [rolePrivileges, setRolePrivileges] = useState<RolePrivilege[]>([]);
  const [usersInRole, setUsersInRole] = useState<number>(0);

  // Filter & UI states
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modifiedPrivileges, setModifiedPrivileges] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      fetchUsersInRole(selectedRoleId);
    }
  }, [selectedRoleId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [featuresRes, rolesRes, deptsRes] = await Promise.all([
        ApiClient.get('/get-features'),
        ApiClient.get('/get-roles'),
        ApiClient.get('/get-departments'),
      ]);

      if (featuresRes.data.status === 'success') {
        const featuresData = featuresRes.data.data || [];
        setFeatures(featuresData);

        const privilegePromises = featuresData.map((feature: Feature) =>
          ApiClient.get(`/get-feature-privileges?feature_id=${feature.id}`)
        );
        const privilegeResults = await Promise.all(privilegePromises);
        const allPrivs: FeaturePrivilege[] = privilegeResults.flatMap(res => res.data.data || []);
        setAllPrivileges(allPrivs);
      }

      if (rolesRes.data.status === 'success') {
        setRoles(rolesRes.data.data || []);
      }

      if (deptsRes.data.status === 'success') {
        setDepartments(deptsRes.data.data || []);
      }

      await fetchRolePrivileges();
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchRolePrivileges = async () => {
    try {
      const response = await ApiClient.get('/get-role-privileges');
      if (response.data.status === 'success') {
        setRolePrivileges(response.data.data || []);
      }
    } catch (error) {
      console.warn('Role privileges endpoint not available:', error);
      setRolePrivileges([]);
    }
  };

  const fetchUsersInRole = async (roleId: number) => {
    try {
      const response = await ApiClient.get('/get-users-by-role');
      if (response.data.status === 'success') {
        const users = response.data.data || [];
        const count = users.filter((u: { role_id: number }) => u.role_id === roleId).length;
        setUsersInRole(count);
      }
    } catch (error) {
      console.error('Error fetching users in role:', error);
      setUsersInRole(0);
    }
  };

  const selectedRole = useMemo(() => {
    return roles.find(r => r.id === selectedRoleId) || null;
  }, [roles, selectedRoleId]);

  const selectedRolePrivileges = useMemo(() => {
    if (!selectedRoleId) {
      return new Set<number>();
    }
    return new Set(
      rolePrivileges
        .filter(rp => rp.role_id === selectedRoleId && rp.status === 'active')
        .map(rp => rp.feature_privilege_id)
    );
  }, [rolePrivileges, selectedRoleId]);

  const groupedPrivileges: GroupedPrivileges[] = useMemo(() => {
    return features.map(feature => ({
      feature,
      privileges: allPrivileges.filter(p => p.feature_id === feature.id && p.status === 'active'),
    }));
  }, [features, allPrivileges]);

  const filteredRoles = useMemo(() => {
    let filtered = roles;

    if (selectedDepartmentId) {
      filtered = filtered.filter(
        r =>
          String((r as Role & { department_id?: number }).department_id || '') ===
          selectedDepartmentId
      );
    }

    const query = searchQuery.toLowerCase();
    if (query) {
      filtered = filtered.filter(
        role =>
          role.role_name.toLowerCase().includes(query) ||
          (role.description && role.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [roles, selectedDepartmentId, searchQuery]);

  const handleTogglePrivilege = (privilegeId: number) => {
    const newModified = new Set(modifiedPrivileges);
    if (newModified.has(privilegeId)) {
      newModified.delete(privilegeId);
    } else {
      newModified.add(privilegeId);
    }
    setModifiedPrivileges(newModified);
  };

  const handleSaveChanges = async () => {
    if (!selectedRoleId || modifiedPrivileges.size === 0) {
      return;
    }

    setSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      modifiedPrivileges.forEach(privilegeId => {
        const hasPrivilege = selectedRolePrivileges.has(privilegeId);

        if (hasPrivilege) {
          promises.push(
            ApiClient.post('/remove-role-privilege', {
              role_id: selectedRoleId,
              feature_privilege_id: privilegeId,
            })
          );
        } else {
          promises.push(
            ApiClient.post('/assign-role-privilege', {
              role_id: selectedRoleId,
              feature_privilege_id: privilegeId,
            })
          );
        }
      });

      await Promise.all(promises);
      newToast('success', 'Role privileges updated successfully');
      setModifiedPrivileges(new Set());
      await fetchRolePrivileges();
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSaving(false);
  };

  const handleCancelChanges = () => {
    setModifiedPrivileges(new Set());
  };

  const hasChanges = modifiedPrivileges.size > 0;

  if (loading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Manage Role Privileges</Text>
        </div>
        <ContentCard>
          <div className={styles.loadingContainer}>
            <Spinner label="Loading role privileges..." />
          </div>
        </ContentCard>
      </div>
    );
  }

  return (
    <PermissionGate privilege="manage-user-privileges" showDenied>
      <div>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Manage Role Privileges</Text>
          <Text className={styles.pageSubtitle}>
            Assign privileges to roles - all users with this role will inherit these permissions
          </Text>
        </div>

        <div className={styles.filtersSection}>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>Filter by Department</Text>
            <Dropdown
              placeholder="All Departments"
              value={
                selectedDepartmentId
                  ? departments.find(d => String(d.id) === selectedDepartmentId)?.department_name
                  : undefined
              }
              onOptionSelect={(_, data) => {
                setSelectedDepartmentId(data.optionValue || '');
                setSelectedRoleId(null);
              }}
            >
              <Option value="">All Departments</Option>
              {departments.map(dept => (
                <Option key={dept.id} value={String(dept.id)}>
                  {dept.department_name}
                </Option>
              ))}
            </Dropdown>
          </div>

          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>Search Roles</Text>
            <Input
              placeholder="Search by role name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              contentBefore={<SearchRegular />}
            />
          </div>
        </div>

        <ContentCard>
          <div className={styles.container}>
            <div className={styles.roleListPanel}>
              <Text style={{ fontSize: '14px', fontWeight: '600', color: '#323130' }}>
                Roles ({filteredRoles.length})
              </Text>

              <div className={styles.roleList}>
                {filteredRoles.map(role => (
                  <div
                    key={role.id}
                    className={`${styles.roleCard} ${
                      selectedRoleId === role.id ? styles.roleCardSelected : ''
                    }`}
                    onClick={() => setSelectedRoleId(role.id)}
                    style={selectedRoleId === role.id ? { border: '2px solid #008272' } : undefined}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <OrganizationRegular />
                      <div style={{ flex: 1 }}>
                        <Text className={styles.roleName}>{role.role_name}</Text>
                        <Text className={styles.roleDepartment}>
                          {(role as Role & { department_name?: string }).department_name ||
                            'No Department'}
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.privilegePanel}>
              {!selectedRole ? (
                <div className={styles.emptyState}>
                  <ShieldCheckmarkRegular style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Text style={{ fontSize: '16px', fontWeight: '600' }}>
                    Select a role to manage privileges
                  </Text>
                  <Text style={{ fontSize: '14px', marginTop: '8px' }}>
                    Choose a role from the list to view and modify its privileges
                  </Text>
                </div>
              ) : (
                <>
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <div>
                        <Text className={styles.sectionTitle}>
                          Privileges for {selectedRole.role_name}
                        </Text>
                        {selectedRole.description && (
                          <Text style={{ fontSize: '13px', color: '#605e5c', marginTop: '4px' }}>
                            {selectedRole.description}
                          </Text>
                        )}
                      </div>
                      <Badge appearance="tint" color="brand">
                        {selectedRolePrivileges.size} assigned
                      </Badge>
                    </div>

                    <div className={styles.infoBox}>
                      <PeopleRegular style={{ fontSize: '20px', color: '#0078d4' }} />
                      <Text className={styles.infoText}>
                        <strong>{usersInRole} user(s)</strong> will inherit these privileges through
                        this role
                      </Text>
                    </div>

                    <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                      Click privileges to add or remove them from this role. All users with this
                      role will inherit these permissions.
                    </Text>

                    {groupedPrivileges.map(group => (
                      <div key={group.feature.id} className={styles.featureGroup}>
                        <div className={styles.featureTitle}>
                          <ShieldCheckmarkRegular />
                          {group.feature.name}
                          <Badge appearance="outline" size="small">
                            {group.privileges.filter(p => selectedRolePrivileges.has(p.id)).length}{' '}
                            of {group.privileges.length}
                          </Badge>
                        </div>

                        <div className={styles.privilegeList}>
                          {group.privileges.map(privilege => {
                            const hasPrivilege = selectedRolePrivileges.has(privilege.id);
                            const isModified = modifiedPrivileges.has(privilege.id);
                            const willHave = isModified ? !hasPrivilege : hasPrivilege;

                            return (
                              <div
                                key={privilege.id}
                                className={`${styles.privilegeItem} ${
                                  willHave ? styles.privilegeItemAssigned : ''
                                }`}
                                onClick={() => handleTogglePrivilege(privilege.id)}
                              >
                                {willHave ? (
                                  <CheckmarkCircleRegular style={{ color: '#008272' }} />
                                ) : (
                                  <DismissCircleRegular style={{ color: '#a19f9d' }} />
                                )}
                                <Text className={styles.privilegeText}>{privilege.title}</Text>
                                {isModified && (
                                  <Badge appearance="filled" color="warning" size="small">
                                    Modified
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasChanges && (
                    <div className={styles.actionButtons}>
                      <Button
                        appearance="primary"
                        onClick={handleSaveChanges}
                        disabled={saving}
                        icon={saving ? <Spinner size="tiny" /> : undefined}
                      >
                        {saving ? 'Saving...' : `Save Changes (${modifiedPrivileges.size})`}
                      </Button>
                      <Button
                        appearance="secondary"
                        onClick={handleCancelChanges}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default RolePrivilegesManagementFluentUI;
